import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components/macro";
import { MdEdit, MdDelete } from "react-icons/md";
import { StyledInput, FormActions } from "../Form/FormControls";
import Button from "../Button";
import DataTable from "../Table/DataTable";
import RowActionsMenu, { MenuItem } from "../Table/RowActionsMenu";
import CitationLink from "./CitationLink";
import { TableWrap, EditFields, ActionButtons } from "./citationStyles";
import { citationIsValid, isSafeCitationUrl } from "./citationHelpers";

const InvalidUrlHint = styled.small`
  display: block;
  color: ${(props) => props.theme.error ?? "#c0392b"};
`;

// Create-path citation editor. Mirrors the edit path exactly: staged citations
// collapse to a clickable name with a kebab (Edit/Remove) and expand back to the
// label/URL editor in place — inside the table — while being added or edited. A
// brand-new row shows Create/Cancel; editing an existing row shows Save/Cancel.
// Citations staged here are always client citations (the server enforces this);
// ACES citations are never set in the UI.
const SelectCitations = ({
  currentCitations = [],
  updateCitations,
  dispatchType,
}) => {
  const nextRowKey = useRef(0);

  // Committed (collapsed) staged citations — the analog of the edit path's saved
  // server citations.
  const [rows, setRows] = useState(() =>
    currentCitations.length
      ? currentCitations.map((c) => ({
          key: nextRowKey.current++,
          title: c?.title ?? "",
          url: c?.url ?? "",
        }))
      : []
  );
  // Draft values per key while a committed row is being edited (presence == editing).
  const [drafts, setDrafts] = useState({});
  // Brand-new rows being entered in the table (not yet committed).
  const [newRows, setNewRows] = useState([]);

  // Latest values, so the stable callbacks below can read current state at click
  // time without depending on (and rebuilding the columns for) it.
  const rowsRef = useRef(rows);
  const draftsRef = useRef(drafts);
  const newRowsRef = useRef(newRows);
  useEffect(() => {
    rowsRef.current = rows;
    draftsRef.current = drafts;
    newRowsRef.current = newRows;
  });

  // Push staged citations to the parent reducer only when the committed set changes,
  // not on every keystroke. Only complete rows (label + safe http/https URL) ride
  // along, matching the edit path and the server's validation.
  const lastPayloadRef = useRef(null);
  useEffect(() => {
    if (!updateCitations || !dispatchType) {
      return;
    }
    const payload = rows
      .filter((r) => citationIsValid(r))
      .map((r) => ({ title: r.title.trim(), url: r.url.trim() }));
    const serialized = JSON.stringify(payload);
    if (serialized === lastPayloadRef.current) {
      return;
    }
    lastPayloadRef.current = serialized;
    updateCitations({ type: dispatchType, payload });
  }, [rows, updateCitations, dispatchType]);

  // --- Brand-new (uncommitted) in-table rows -------------------------------
  const addNewRow = useCallback(
    () =>
      setNewRows((prev) => [
        ...prev,
        { key: nextRowKey.current++, title: "", url: "" },
      ]),
    []
  );

  const updateNewRow = useCallback(
    (key, field, value) =>
      setNewRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
      ),
    []
  );

  const cancelNew = useCallback(
    (key) => setNewRows((prev) => prev.filter((r) => r.key !== key)),
    []
  );

  const createNew = useCallback((key) => {
    const row = newRowsRef.current.find((r) => r.key === key);
    if (!row || !citationIsValid(row)) {
      return;
    }
    setRows((prev) => [
      ...prev,
      { key: row.key, title: row.title, url: row.url },
    ]);
    setNewRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  // --- Committed rows (collapse / edit) ------------------------------------
  const startEditing = useCallback((key) => {
    const row = rowsRef.current.find((r) => r.key === key);
    if (!row) {
      return;
    }
    setDrafts((d) => ({ ...d, [key]: { title: row.title, url: row.url } }));
  }, []);

  const updateDraft = useCallback(
    (key, field, value) =>
      setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } })),
    []
  );

  const cancelEditing = useCallback(
    (key) =>
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      }),
    []
  );

  const saveExisting = useCallback((key) => {
    const draft = draftsRef.current[key];
    if (draft) {
      setRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, ...draft } : r))
      );
    }
    setDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }, []);

  const removeExisting = useCallback((key) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
    setDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }, []);

  // Flatten the live edit values + flags into the row data so cells read from
  // `original` rather than a closure — keeping Cell identities stable (focus).
  // Committed rows first, then the brand-new rows still being added.
  const tableRows = [
    ...rows.map((r) => {
      const draft = drafts[r.key];
      return {
        key: r.key,
        isNew: false,
        editing: !!draft,
        title: draft ? draft.title : r.title,
        url: draft ? draft.url : r.url,
      };
    }),
    ...newRows.map((r) => ({
      key: r.key,
      isNew: true,
      editing: true,
      title: r.title,
      url: r.url,
    })),
  ];

  const columns = useMemo(
    () => [
      {
        Header: "Citation",
        id: "citation",
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          if (!original.editing) {
            return <CitationLink title={original.title} url={original.url} />;
          }
          const onField = (field) => (e) =>
            original.isNew
              ? updateNewRow(original.key, field, e.target.value)
              : updateDraft(original.key, field, e.target.value);
          const urlInvalid =
            !!original.url?.trim() && !isSafeCitationUrl(original.url?.trim());
          return (
            <EditFields>
              <StyledInput
                label="Citation Name"
                placeholder="Citation label"
                value={original.title}
                onChange={onField("title")}
              />
              <div>
                <StyledInput
                  label="URL"
                  placeholder="https://..."
                  value={original.url}
                  onChange={onField("url")}
                />
                {urlInvalid ? (
                  <InvalidUrlHint>
                    Enter an absolute http or https URL
                  </InvalidUrlHint>
                ) : null}
              </div>
            </EditFields>
          );
        },
      },
      {
        Header: "Actions",
        id: "actions",
        align: "right",
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          if (original.editing) {
            return (
              <ActionButtons>
                <Button
                  type="button"
                  size="small"
                  disabled={!citationIsValid(original)}
                  onClick={() =>
                    original.isNew
                      ? createNew(original.key)
                      : saveExisting(original.key)
                  }
                >
                  {original.isNew ? "Create" : "Save"}
                </Button>
                <Button
                  type="button"
                  size="small"
                  onClick={() =>
                    original.isNew
                      ? cancelNew(original.key)
                      : cancelEditing(original.key)
                  }
                >
                  Cancel
                </Button>
              </ActionButtons>
            );
          }
          return (
            <div style={{ textAlign: "right" }}>
              <RowActionsMenu ariaLabel="Citation actions">
                <MenuItem
                  type="button"
                  onClick={() => startEditing(original.key)}
                >
                  <MdEdit /> Edit
                </MenuItem>
                <MenuItem
                  danger
                  type="button"
                  onClick={() => removeExisting(original.key)}
                >
                  <MdDelete /> Remove
                </MenuItem>
              </RowActionsMenu>
            </div>
          );
        },
      },
    ],
    [
      updateNewRow,
      updateDraft,
      createNew,
      cancelNew,
      saveExisting,
      cancelEditing,
      startEditing,
      removeExisting,
    ]
  );

  return (
    <>
      <h3>Citations</h3>

      {tableRows.length ? (
        <TableWrap>
          <DataTable
            mode="clientSide"
            hidePagination
            disableSortBy
            noMargin
            data={tableRows}
            columns={columns}
          />
        </TableWrap>
      ) : null}

      <FormActions>
        <Button type="button" onClick={addNewRow}>
          Add Citation
        </Button>
      </FormActions>
    </>
  );
};

export default SelectCitations;
